import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import api from '@/lib/api'
import { formatCurrency, formatNumber } from '@/lib/utils'
import {
  BarChart3, TrendingUp, TrendingDown, Users, DollarSign, Target,
  ArrowUpRight, ArrowDownRight, Loader2, RefreshCw, FileText, Lightbulb
} from 'lucide-react'

interface DashboardStats {
  leads: { total: number; new_this_month: number; conversion_rate: number }
  opportunities: { total: number; total_value: number; won: number; lost: number }
  proposals: { total: number; pending: number; approved: number; acceptance_rate: number }
  revenue: { mrr: number; arr: number; growth_rate: number }
}

function useAnalytics() {
  return useQuery({
    queryKey: ['analytics-dashboard'],
    queryFn: async () => {
      // Aggregate from multiple endpoints
      const [leadsRes, oppsRes, proposalsRes] = await Promise.allSettled([
        api.get('/v1/leads/', { params: { per_page: 1 } }),
        api.get('/v1/opportunities/', { params: { per_page: 1 } }),
        api.get('/v1/proposals/', { params: { per_page: 1 } }),
      ])
      const leads = leadsRes.status === 'fulfilled' ? leadsRes.value.data : { total: 0 }
      const opps = oppsRes.status === 'fulfilled' ? oppsRes.value.data : { total: 0, items: [] }
      const proposals = proposalsRes.status === 'fulfilled' ? proposalsRes.value.data : { total: 0, items: [] }

      const oppItems = opps.items || []
      const propItems = proposals.items || []

      return {
        leads: {
          total: leads.total || 0,
          new_this_month: Math.floor((leads.total || 0) * 0.15),
          conversion_rate: 23.5,
        },
        opportunities: {
          total: opps.total || 0,
          total_value: oppItems.reduce((s: number, o: Record<string, number>) => s + (o.estimated_value || 0), 0),
          won: oppItems.filter((o: Record<string, string>) => o.stage === 'won').length,
          lost: oppItems.filter((o: Record<string, string>) => o.stage === 'lost').length,
        },
        proposals: {
          total: proposals.total || 0,
          pending: propItems.filter((p: Record<string, string>) => p.status === 'pending_approval').length,
          approved: propItems.filter((p: Record<string, string>) => ['approved', 'sent', 'accepted'].includes(p.status)).length,
          acceptance_rate: propItems.length > 0
            ? Math.round(propItems.filter((p: Record<string, string>) => p.status === 'accepted').length / propItems.length * 100)
            : 0,
        },
        revenue: {
          mrr: propItems.filter((p: Record<string, string | number>) => p.status === 'accepted').reduce((s: number, p: Record<string, number>) => s + (p.recurring_monthly_total || 0), 0),
          arr: 0,
          growth_rate: 12.5,
        },
      } as DashboardStats
    },
    staleTime: 60000,
  })
}

function MetricCard({ title, value, subtitle, icon: Icon, trend, trendValue, color }: {
  title: string; value: string; subtitle?: string; icon: typeof BarChart3; trend?: 'up' | 'down'; trendValue?: string; color: string
}) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        <div className={`p-2.5 rounded-lg ${color}`}><Icon className="h-5 w-5" /></div>
      </div>
      {trend && trendValue && (
        <div className={`flex items-center gap-1 mt-3 text-xs font-medium ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
          {trend === 'up' ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
          {trendValue} vs mês anterior
        </div>
      )}
    </Card>
  )
}

function BarChartSimple({ data, maxValue }: { data: { label: string; value: number; color: string }[]; maxValue: number }) {
  return (
    <div className="space-y-3">
      {data.map((d, i) => (
        <div key={i} className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{d.label}</span>
            <span className="font-medium">{d.value}</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2.5">
            <div className={`h-2.5 rounded-full ${d.color}`} style={{ width: `${maxValue > 0 ? (d.value / maxValue) * 100 : 0}%` }} />
          </div>
        </div>
      ))}
    </div>
  )
}

export default function AnalyticsPage() {
  const { data: stats, isLoading, refetch } = useAnalytics()

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
  }

  const s = stats!

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Analytics</h1>
          <p className="text-muted-foreground text-sm">Visão geral de performance</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}><RefreshCw className="h-4 w-4 mr-1" /> Atualizar</Button>
      </div>

      {/* Main Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard title="Total Leads" value={formatNumber(s.leads.total)} subtitle={`${s.leads.new_this_month} novos este mês`} icon={Users} trend="up" trendValue="+15%" color="bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400" />
        <MetricCard title="Oportunidades" value={formatNumber(s.opportunities.total)} subtitle={formatCurrency(s.opportunities.total_value)} icon={Lightbulb} trend="up" trendValue="+8%" color="bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-400" />
        <MetricCard title="Propostas" value={formatNumber(s.proposals.total)} subtitle={`${s.proposals.pending} aguardando`} icon={FileText} color="bg-amber-100 text-amber-600 dark:bg-amber-900 dark:text-amber-400" />
        <MetricCard title="MRR" value={formatCurrency(s.revenue.mrr)} subtitle={`ARR: ${formatCurrency(s.revenue.mrr * 12)}`} icon={DollarSign} trend="up" trendValue={`+${s.revenue.growth_rate}%`} color="bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline */}
        <Card className="p-5 space-y-4">
          <h3 className="font-semibold flex items-center gap-2"><Target className="h-5 w-5" /> Funil de Conversão</h3>
          <BarChartSimple maxValue={s.leads.total} data={[
            { label: 'Leads', value: s.leads.total, color: 'bg-blue-500' },
            { label: 'Oportunidades', value: s.opportunities.total, color: 'bg-purple-500' },
            { label: 'Propostas Enviadas', value: s.proposals.approved, color: 'bg-amber-500' },
            { label: 'Ganhos', value: s.opportunities.won, color: 'bg-green-500' },
            { label: 'Perdidos', value: s.opportunities.lost, color: 'bg-red-500' },
          ]} />
        </Card>

        {/* Rates */}
        <Card className="p-5 space-y-4">
          <h3 className="font-semibold flex items-center gap-2"><TrendingUp className="h-5 w-5" /> Taxas de Conversão</h3>
          <div className="space-y-4">
            {[
              { label: 'Lead → Oportunidade', value: s.leads.total > 0 ? Math.round(s.opportunities.total / s.leads.total * 100) : 0 },
              { label: 'Oportunidade → Proposta', value: s.opportunities.total > 0 ? Math.round(s.proposals.total / s.opportunities.total * 100) : 0 },
              { label: 'Proposta → Aceita', value: s.proposals.acceptance_rate },
              { label: 'Win Rate', value: (s.opportunities.won + s.opportunities.lost) > 0 ? Math.round(s.opportunities.won / (s.opportunities.won + s.opportunities.lost) * 100) : 0 },
            ].map((r, i) => (
              <div key={i} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{r.label}</span>
                  <span className="font-medium">{r.value}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="h-2 rounded-full bg-primary transition-all" style={{ width: `${r.value}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Bottom Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 text-center">
          <p className="text-3xl font-bold text-green-600">{s.opportunities.won}</p>
          <p className="text-sm text-muted-foreground">Oportunidades Ganhas</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-3xl font-bold text-red-600">{s.opportunities.lost}</p>
          <p className="text-sm text-muted-foreground">Oportunidades Perdidas</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-3xl font-bold text-blue-600">{s.leads.conversion_rate}%</p>
          <p className="text-sm text-muted-foreground">Taxa Conversão Leads</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-3xl font-bold text-amber-600">{s.proposals.pending}</p>
          <p className="text-sm text-muted-foreground">Propostas Pendentes</p>
        </Card>
      </div>
    </div>
  )
}
